import { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import type { PromptSectionName } from '@/types/api/prompt-versions';
import { PROMPT_SECTION_LABELS, PROMPT_SECTION_PLACEHOLDERS } from '@/types/api/prompt-versions';

interface PromptSectionEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  section: PromptSectionName | null;
  value: string | null;
  onSave: (section: PromptSectionName, value: string | null) => void;
  isSaving: boolean;
}

export function PromptSectionEditor({
  open,
  onOpenChange,
  section,
  value,
  onSave,
  isSaving,
}: PromptSectionEditorProps) {
  const [editedValue, setEditedValue] = useState<string>('');

  // Sync state when opening the editor
  useEffect(() => {
    if (open && section) {
      setEditedValue(value || '');
    }
  }, [open, section, value]);

  const handleSave = () => {
    if (!section) return;
    // If empty, save as null (will use default)
    const valueToSave = editedValue.trim() || null;
    onSave(section, valueToSave);
  };

  const handleDiscard = () => {
    setEditedValue(value || '');
    onOpenChange(false);
  };

  const hasChanges = (editedValue || '') !== (value || '');

  if (!section) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[90vh] flex flex-col">
        <SheetHeader>
          <SheetTitle>{PROMPT_SECTION_LABELS[section]}</SheetTitle>
          <SheetDescription>
            Personalize esta seção do prompt. Deixe em branco para usar o valor padrão.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 py-4 overflow-hidden">
          <Textarea
            value={editedValue}
            onChange={(e) => setEditedValue(e.target.value)}
            placeholder={PROMPT_SECTION_PLACEHOLDERS[section]}
            className="h-full min-h-[200px] resize-none text-base"
          />
        </div>

        <SheetFooter className="flex-row gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={handleDiscard}
            disabled={isSaving}
          >
            {hasChanges ? 'Descartar' : 'Cancelar'}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
